import { MenuItem } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { MedicationOption, OTHER_OPTION } from './medicationFormOptions';
import { selectTestId } from './selectTestId';

interface MedicationEnumFieldProps<T extends string> {
  name: string;
  customName: string;
  label: string;
  customLabel: string;
  options: MedicationOption<T>[];
  disabled?: boolean;
  required?: boolean;
}

/**
 * A list plus the escape hatch beside it. Picking "Inne — wpisz ręcznie"
 * reveals the free-text twin, which the schema then requires: the value is
 * off the list, but it is still recorded in its own field rather than buried
 * in the notes.
 */
export default function MedicationEnumField<T extends string>({
  name,
  customName,
  label,
  customLabel,
  options,
  disabled,
  required,
}: MedicationEnumFieldProps<T>) {
  const { watch } = useFormContext();
  const isCustom = watch(name) === OTHER_OPTION;

  return (
    <>
      <RHFSelect
        name={name}
        label={label}
        required={required}
        disabled={disabled}
        SelectProps={{
          native: false,
          SelectDisplayProps: selectTestId(`medication-select-${name}`),
        }}
      >
        <MenuItem value="">-</MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            data-testid={`medication-option-${option.value}`}
          >
            {option.label}
          </MenuItem>
        ))}
      </RHFSelect>

      {isCustom && (
        <RHFTextField
          name={customName}
          label={customLabel}
          required
          disabled={disabled}
        />
      )}
    </>
  );
}
